/**
 * AboutImageGrid Component
 *
 * Diamond Architecture: Image mosaic matching Rails static/about/_image_grid.html.erb
 *
 * Desktop: 4-column grid (33.33% | 22.22% | 22.22% | 22.22%)
 * Mobile: 2-column grid (60% | 40%)
 *
 * Includes decorative SVG illustrations (Bucket B - static images)
 */
import Image from '@/shared/components/ui/Image';
import { useTranslations } from '@/shared/i18n/useTranslations';
import { assetPath, type AssetPath, type LocalizedText } from '@letsgethome/types/branded';
import { cn } from '@letsgethome/ui/lib/utils';
import { Z_LAYERS } from '@letsgethome/ui/z-layers';

export function AboutImageGrid() {
  const t = useTranslations('about');

  return (
    <>
      {/* Desktop Grid (hidden on mobile) */}
      <div className="hidden md:grid w-full rounded-2xl shadow-2xl overflow-hidden grid-cols-[33.33%_22.22%_22.22%_22.22%]">
        {/* Row 1 */}
        <ImageCell
          src={assetPath('/about/a1.webp')}
          alt={t('imageGrid.photoAlt.couple')}
          aspectRatio="3/2"
          decoSrc={assetPath('/about/icons/deco-5.svg')}
          decoPosition="top-[5%] left-[10%] w-[15%] h-[20%]"
          priority
        />
        <QuoteCell text={t('imageGrid.quote')} />
        <ImageCell
          src={assetPath('/about/c1.webp')}
          alt={t('imageGrid.photoAlt.hotpot')}
          aspectRatio="square"
          decoSrc={assetPath('/about/icons/deco-3.svg')}
          decoPosition="top-[15%] left-[30%] w-[15%] h-[15%]"
        />
        <ArcCell />
        {/* Row 2 */}
        <LogoCell tagline={t('imageGrid.tagline')} />
        <ImageCell
          src={assetPath('/about/b2.webp')}
          alt={t('imageGrid.photoAlt.takeaway')}
          aspectRatio="square"
        />
        <HomeIconsCell />
        <ImageCell
          src={assetPath('/about/d2.webp')}
          alt={t('imageGrid.photoAlt.mahjong')}
          aspectRatio="square"
        />
        {/* Row 3 */}
        <ImageCell
          src={assetPath('/about/a3.webp')}
          alt={t('imageGrid.photoAlt.drawing')}
          aspectRatio="3/2"
          decoSrc={assetPath('/about/icons/deco-2.svg')}
          decoPosition="top-0 right-0 w-[20%] h-[30%]"
        />
        <div className="aspect-square" /> {/* Empty cell */}
        <ImageCell
          src={assetPath('/about/c3.webp')}
          alt={t('imageGrid.photoAlt.team')}
          aspectRatio="square"
          decoSrc={assetPath('/about/icons/deco-4.svg')}
          decoPosition="top-[15%] right-[15%] w-[25%] h-[25%]"
        />
        <div className="aspect-square" /> {/* Empty cell */}
      </div>

      {/* Mobile Grid (hidden on desktop) */}
      <div className="md:hidden w-full rounded-2xl shadow-2xl overflow-hidden grid grid-cols-[60%_40%]">
        {/* Row 1 */}
        <ImageCell
          src={assetPath('/about/a1.webp')}
          alt={t('imageGrid.photoAlt.couple')}
          aspectRatio="3/2"
          priority
        />
        <QuoteCell text={t('imageGrid.quote')} />

        {/* Row 2 */}
        <LogoCell tagline={t('imageGrid.tagline')} />
        <ImageCell
          src={assetPath('/about/c3.webp')}
          alt={t('imageGrid.photoAlt.team')}
          aspectRatio="square"
        />

        {/* Row 3 */}
        <ImageCell
          src={assetPath('/about/a3.webp')}
          alt={t('imageGrid.photoAlt.drawing')}
          aspectRatio="3/2"
        />
        <HomeIconsCell />
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Cell Components
// ---------------------------------------------------------------------------

function ImageCell({
  src,
  alt,
  aspectRatio,
  decoSrc,
  decoPosition,
  priority = false,
}: {
  src: AssetPath;
  alt: LocalizedText;
  aspectRatio: '3/2' | 'square';
  decoSrc?: AssetPath;
  decoPosition?: string;
  priority?: boolean;
}) {
  const aspect = aspectRatio === '3/2' ? 'aspect-[3/2]' : 'aspect-square';

  return (
    <div className={`relative ${aspect} flex`}>
      <Image
        src={src}
        alt={alt}
        fill
        className="object-cover"
        sizes="(max-width: 768px) 60vw, 33vw"
        priority={priority}
        unoptimized
      />
      {decoSrc && (
        <Image
          src={decoSrc}
          decorative
          width={48}
          height={48}
          className={`absolute ${decoPosition}`}
          priority={priority}
          unoptimized
        />
      )}
    </div>
  );
}

function QuoteCell({ text }: { text: LocalizedText }) {
  return (
    <div className="relative aspect-square flex items-center p-2 md:p-4 bg-background">
      <p className="text-sm md:text-lg font-medium">{text}</p>
      <Image
        src={assetPath('/about/icons/deco-1.svg')}
        decorative
        width={48}
        height={48}
        className="absolute bottom-[4%] right-[8%] w-[25%] h-[25%]"
        priority
        unoptimized
      />
    </div>
  );
}

function LogoCell({ tagline }: { tagline: LocalizedText }) {
  return (
    <div className="aspect-[3/2] flex items-center p-3 md:p-4 bg-background">
      <div className="flex flex-col gap-2 md:gap-4">
        <Image
          src={assetPath('/brand/logo-horizontal-color.webp')}
          decorative
          width={200}
          height={40}
          className="w-1/2 h-auto"
          unoptimized
        />
        <span className="text-muted-foreground font-bold text-sm md:text-base">{tagline}</span>
      </div>
    </div>
  );
}

function HomeIconsCell() {
  return (
    <div className="relative aspect-square flex bg-background">
      {/* Stacked "HOME" text icons in brand colors */}
      <Image
        src={assetPath('/about/icons/home.svg')}
        decorative
        width={100}
        height={30}
        className="absolute w-[50%] h-[16%] left-[25%] top-[34%]"
        style={{
          filter:
            'brightness(0) saturate(100%) invert(48%) sepia(79%) saturate(2476%) hue-rotate(326deg) brightness(104%) contrast(101%)',
        }}
        unoptimized
      />
      <Image
        src={assetPath('/about/icons/home.svg')}
        decorative
        width={100}
        height={30}
        className="absolute w-[50%] h-[16%] left-[25%] top-[42%]"
        style={{
          filter:
            'brightness(0) saturate(100%) invert(56%) sepia(52%) saturate(3969%) hue-rotate(295deg) brightness(100%) contrast(101%)',
        }}
        unoptimized
      />
      <Image
        src={assetPath('/about/icons/home.svg')}
        decorative
        width={100}
        height={30}
        className="absolute w-[50%] h-[16%] left-[25%] top-[50%]"
        unoptimized
      />
      <Image
        src={assetPath('/about/icons/hand.svg')}
        decorative
        width={40}
        height={50}
        className={cn(
          'absolute w-[20%] h-[25%] top-[28%] left-[8%]',
          Z_LAYERS.decorativeBackdrop.className
        )}
        unoptimized
      />
    </div>
  );
}

function ArcCell() {
  return (
    <div className="aspect-square flex items-end bg-background">
      <Image
        src={assetPath('/about/icons/arc.svg')}
        decorative
        width={132}
        height={108}
        className="w-full h-auto"
        priority
        unoptimized
      />
    </div>
  );
}
